import React, {
	Fragment,
	useCallback,
	type ComponentType,
	type ReactNode,
	useEffect,
	forwardRef,
	type Ref,
} from 'react';
import { styled } from '@compiled/react';
import Button, { type Appearance } from '@atlaskit/button';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import MediaServicesActualSizeIcon from '@atlaskit/icon/glyph/media-services/actual-size';
import VidFullScreenOffIcon from '@atlaskit/icon/glyph/vid-full-screen-off';
import { ModalHeader as AKModalHeader, ModalTitle as AKModalTitle } from '@atlaskit/modal-dialog';
import { N40A, N800 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import ToolTip from '@atlaskit/tooltip';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { TransitionState } from '@atlassian/jira-minimizable-modal/src/controllers/use-minimizable-modal/types.tsx';
import { useNestedModalDetector } from '@atlassian/jira-minimizable-modal/src/controllers/use-nested-modal-detector/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch';
import { useEnterFocusState } from '../../../controllers/enter-focus-state';
import { useMinimizableModal } from '../../../controllers/use-minimizable-modal';
import type { MinimizableModalView, ModalHeaderProps } from '../../../types';
import { MinimizeIcon } from './assets/minimize-icon';
import messages from './messages';
import type { ViewChangerProps } from './types';

export const ViewChanger = ({
	isMini = false,
	ViewChangerContainer,
	dismissModalEntryPointWrapper,
}: ViewChangerProps) => {
	const {
		view,
		setView,
		onClose,
		isTransitionInState,
		setShouldFocusChanger,
		displayMinimizableButtons,
	} = useMinimizableModal();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { minimizedButtonRef, modalButtonRef, miniButtonRef, wrapperRef, onKeyDown } =
		useEnterFocusState();

	const onViewChange = useCallback(
		(newView: MinimizableModalView) => () => {
			if (!isTransitionInState([view], TransitionState.ENTERED)) return;
			setShouldFocusChanger(true);
			setView(newView);
			const analyticsEvent = createAnalyticsEvent({
				action: 'button clicked',
				actionSubject: newView,
			});
			fireUIAnalytics(analyticsEvent, {
				source: 'globalIssueCreateModal',
				previousGICViewState: view,
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[setView, isTransitionInState],
	);

	const onUpdateView = useCallback(
		(newView: MinimizableModalView) => {
			if (!isTransitionInState([view], TransitionState.ENTERED)) return;
			setShouldFocusChanger(true);
			setView(newView);
			const analyticsEvent = createAnalyticsEvent({
				action: 'button clicked',
				actionSubject: newView,
			});
			fireUIAnalytics(analyticsEvent, {
				source: 'globalIssueCreateModal',
				previousGICViewState: view,
			});
		},
		[isTransitionInState, view, setShouldFocusChanger, setView, createAnalyticsEvent],
	);

	const onViewChangeToMinimized = useCallback(() => onUpdateView('minimized'), [onUpdateView]);
	const onViewChangeToMini = useCallback(() => onUpdateView('mini'), [onUpdateView]);
	const onViewChangeToModal = useCallback(() => onUpdateView('modal'), [onUpdateView]);

	const { shouldDisableViewChangerButtons, customDisabledViewChangerButtonsMessage } =
		useMinimizableModal();

	useEffect(() => {
		if (shouldDisableViewChangerButtons) {
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'disabled',
					actionSubject: 'view changer buttons',
				}),
			);
		}
	}, [
		createAnalyticsEvent,
		customDisabledViewChangerButtonsMessage,
		shouldDisableViewChangerButtons,
	]);

	useNestedModalDetector();

	const Wrapper = ViewChangerContainer || Fragment;

	const ButtonWrapper = isMini ? MiniButtonWrapper : ModalButtonWrapper;
	const expandMessage = messages.fullScreen;
	const dockMessage = messages.fullScreenExit;
	const dockDisabledMessage = messages.fullScreenExitDisabled;

	let closeBtn;
	if (dismissModalEntryPointWrapper) {
		const DismissModalEntryPointWrapper = dismissModalEntryPointWrapper;
		closeBtn = (
			<DismissModalEntryPointWrapper>
				<ToolTip position="top" content={formatMessage(messages.close)}>
					<ButtonWrapper>
						{/* @ts-expect-error - TS2322 - Property 'children' does not exist on type 'IntrinsicAttributes & { appearance?: Appearance | undefined; isMini?: boolean | undefined; } & RefAttributes<HTMLElement>'. */}
						<ToolbarButton
							testId="minimizable-modal.ui.modal-container.modal-header.close-button"
							onClick={onClose}
							spacing="none"
							isMini={isMini}
						>
							<CrossIcon label={formatMessage(messages.close)} />
						</ToolbarButton>
					</ButtonWrapper>
				</ToolTip>
			</DismissModalEntryPointWrapper>
		);
	} else {
		closeBtn = (
			<ToolTip position="top" content={formatMessage(messages.close)}>
				<ButtonWrapper>
					{/* @ts-expect-error - TS2322 - Property 'children' does not exist on type 'IntrinsicAttributes & { appearance?: Appearance | undefined; isMini?: boolean | undefined; } & RefAttributes<HTMLElement>'. */}
					<ToolbarButton
						testId="minimizable-modal.ui.modal-container.modal-header.close-button"
						onClick={onClose}
						spacing="none"
						isMini={isMini}
					>
						<CrossIcon label={formatMessage(messages.close)} />
					</ToolbarButton>
				</ButtonWrapper>
			</ToolTip>
		);
	}

	return (
		<Wrapper>
			<ViewChangerWrapper
				tabIndex={-1}
				ref={wrapperRef}
				isMini={isMini}
				data-testid="minimizable-modal.ui.modal-container.modal-header.view-changer-wrapper"
			>
				{!displayMinimizableButtons ? null : (
					<>
						<ToolTip
							data-testid="minimizable-modal.ui.modal-container.modal-header.tool-tip"
							position="top"
							content={
								shouldDisableViewChangerButtons
									? customDisabledViewChangerButtonsMessage ||
										formatMessage(messages.minimizeDisabled)
									: formatMessage(messages.minimize)
							}
						>
							<ButtonWrapper>
								{/* @ts-expect-error - TS2322 - Property 'children' does not exist on type 'IntrinsicAttributes & { appearance?: Appearance | undefined; isMini?: boolean | undefined; } & RefAttributes<HTMLElement>'. */}
								<ToolbarButton
									testId="minimizable-modal.ui.modal-container.modal-header.minimized-button"
									onClick={
										fg('fix_failed_to_remove_node_error_for_gic')
											? onViewChangeToMinimized
											: onViewChange('minimized')
									}
									onKeyDown={onKeyDown}
									spacing="none"
									ref={minimizedButtonRef}
									isDisabled={shouldDisableViewChangerButtons}
									isMini={isMini}
								>
									<MinimizeIcon label={formatMessage(messages.minimize)} />
								</ToolbarButton>
							</ButtonWrapper>
						</ToolTip>
						{!isMini && (
							<ToolTip
								data-testid="minimizable-modal.ui.modal-container.modal-header.tool-tip"
								position="top"
								content={
									shouldDisableViewChangerButtons
										? customDisabledViewChangerButtonsMessage || formatMessage(dockDisabledMessage)
										: formatMessage(dockMessage)
								}
							>
								<ButtonWrapper>
									{/* @ts-expect-error - TS2322 - Property 'children' does not exist on type 'IntrinsicAttributes & { appearance?: Appearance | undefined; isMini?: boolean | undefined; } & RefAttributes<HTMLElement>'. */}
									<ToolbarButton
										id="minimizable-modal.ui.modal-container.modal-header.mini-button"
										testId="minimizable-modal.ui.modal-container.modal-header.mini-button"
										onClick={
											fg('fix_failed_to_remove_node_error_for_gic')
												? onViewChangeToMini
												: onViewChange('mini')
										}
										onKeyDown={onKeyDown}
										spacing="none"
										ref={miniButtonRef}
										isDisabled={shouldDisableViewChangerButtons}
									>
										<VidFullScreenOffIcon label={formatMessage(dockMessage)} />
									</ToolbarButton>
								</ButtonWrapper>
							</ToolTip>
						)}
						{isMini && (
							<ToolTip position="top" content={formatMessage(expandMessage)}>
								<ButtonWrapper>
									{/* @ts-expect-error - TS2322 - Property 'children' does not exist on type 'IntrinsicAttributes & { appearance?: Appearance | undefined; isMini?: boolean | undefined; } & RefAttributes<HTMLElement>'. */}
									<ToolbarButton
										testId="minimizable-modal.ui.modal-container.modal-header.modal-button"
										onClick={
											fg('fix_failed_to_remove_node_error_for_gic')
												? onViewChangeToModal
												: onViewChange('modal')
										}
										onKeyDown={onKeyDown}
										spacing="none"
										ref={modalButtonRef}
										isMini={isMini}
									>
										<MediaServicesActualSizeIcon label={formatMessage(expandMessage)} />
									</ToolbarButton>
								</ButtonWrapper>
							</ToolTip>
						)}
					</>
				)}
				{closeBtn}
			</ViewChangerWrapper>
		</Wrapper>
	);
};

export const ModalHeader: ComponentType<ModalHeaderProps> = ({
	children,
	ViewChangerContainer,
	dismissModalEntryPointWrapper,
	...rest
}) => (
	<HeaderWrapper>
		<AKModalHeader {...rest}>
			<AKModalTitle>{children}</AKModalTitle>
			<ViewChanger
				ViewChangerContainer={ViewChangerContainer}
				dismissModalEntryPointWrapper={dismissModalEntryPointWrapper}
			/>
		</AKModalHeader>
	</HeaderWrapper>
);

export const MiniHeader: ComponentType<ModalHeaderProps> = ({
	children,
	dismissModalEntryPointWrapper,
	...rest
}) => (
	<MiniHeaderWrapper {...rest}>
		{children}
		<ViewChanger isMini dismissModalEntryPointWrapper={dismissModalEntryPointWrapper} />
	</MiniHeaderWrapper>
);

export const ModalTitle = (props: { children?: ReactNode }) => {
	const { view } = useMinimizableModal();
	if (view === 'mini' || view === 'minimized') {
		return <MiniTitle {...props} />;
	}
	return <div {...props} />;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ViewChangerWrapper = styled.div<{ isMini: boolean }>(
	{
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-end',
		flexShrink: 0,

		'&:focus': {
			outline: 'none',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		props.isMini
			? {
					paddingRight: token('space.050', '4px'),
				}
			: {},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	header: {
		paddingTop: token('space.200', '16px'),
		paddingBottom: token('space.200', '16px'),
		paddingRight: token('space.200', '16px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MiniHeaderWrapper = styled.div({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	justifyContent: 'flex-end',
	color: token('color.text', N800),
	borderBottom: `0.5px solid ${token('color.border', N40A)}`,
	boxShadow: `0 6px 5px -5px ${token('elevation.shadow.overflow.spread', '#091E4229')}`,
	margin: `0 ${token('space.150', '12px')}`,
	padding: `${token('space.250', '20px')} ${token('space.100', '8px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MiniTitle = styled.div({
	color: token('color.text', N800),
	font: token('font.heading.xsmall'),
	padding: `0 ${token('space.100', '8px')}`,
	flexShrink: 1,
	flexGrow: 1,
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ModalButtonWrapper = styled.div({
	margin: `${token('space.075', '6px')} 0 ${token('space.075', '6px')} ${token(
		'space.075',
		'6px',
	)}`,
	lineHeight: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MiniButtonWrapper = styled.div({
	lineHeight: 1,
});

// Setting pointer-events = 'none' to prevent the button from
// blocking the mouse events reaching the wrapper `div`.

const DISABLED_STYLED_NEW = {
	pointsEvents: 'none',
};

// Atlaskit does not have a solution for using tooltips for disabled buttons.
// Workaround to allow tooltip on disabled buttons, technically a design anti-pattern
// The button must be wrapped in a div and the button must have pointer-events set to none when disabled.
// Setting the pointer-events to none sets the pointer to default state, it will look slightly different than before (accepted by design).

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ToolbarButtonExperiment = styled(Button)<{
	isMini?: boolean;
	isVisualRefreshEnabled: boolean;
}>(
	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props: any) => (props.isDisabled ? DISABLED_STYLED_NEW : ''),

	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props: any) =>
		props.isMini && {
			'&&': {
				margin: token('space.025', '2px'),
				padding: token('space.050', '4px'),
			},

			svg: {
				margin: 0,
			},
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @typescript-eslint/no-explicit-any
	(props: any) =>
		props.isMini &&
		props.isVisualRefreshEnabled && {
			border: `1px solid ${token('color.border', N40A)}`,
		},

	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		svg: {
			height: '24px',
			width: '24px',
			margin: token('space.025', '2px'),
		},
	},
);

// styledComponentWithCondition and @compiled returns a functional component so we need to wrap the
// migrated component in another layer in order to set default values for the props
const ToolbarButton = forwardRef(
	(
		{
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			appearance = 'subtle' as Appearance,
			...rest
		}: { appearance?: Appearance; isMini?: boolean },
		ref: Ref<HTMLElement>,
	) => (
		<ToolbarButtonExperiment
			isVisualRefreshEnabled={isVisualRefreshEnabled()}
			appearance={appearance}
			{...rest}
			ref={ref}
		/>
	),
);
